import { useCallback, useState } from 'react'

import { getOntologyServiceBaseUrl } from '../../config'
import { createOntologyHeaders } from '../core'
import { invalidateTagAcquisitionQueries, type TagContainerScope } from './tags-api'

export type RegisterTagsTransport = 'webdev' | 'opc'

export interface RegisterTagsFromGraphArgs {
  nodeIds: string[]
  containerScope?: TagContainerScope
  searchQuery?: string
  preview: boolean
  transport: RegisterTagsTransport
  gatewayUrl?: string
  opcEndpointUrl?: string
  frequencyMs: number
  enabled: boolean
}

export interface RegisterTagsFromGraphItem {
  nodeId: string
  tagPath: string
  vendor: string
  status: string
  outcome: string
  reason: string
}

export interface RegisterTagsFromGraphResult {
  preview: boolean
  resolvedNodeIds: string[]
  selectedCount: number
  registrableCount: number
  excludedCount: number
  createdCount: number
  updatedCount: number
  quarantinedCount: number
  failedCount: number
  items: RegisterTagsFromGraphItem[]
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function registerTagsFromGraphResultFromJson(value: unknown): RegisterTagsFromGraphResult {
  const record = (value ?? {}) as Record<string, unknown>
  const items = Array.isArray(record.items) ? record.items : []
  return {
    preview: record.preview === true,
    resolvedNodeIds: Array.isArray(record.resolved_node_ids)
      ? record.resolved_node_ids.filter((id): id is string => typeof id === 'string')
      : [],
    selectedCount: num(record.selected_count),
    registrableCount: num(record.registrable_count),
    excludedCount: num(record.excluded_count),
    createdCount: num(record.created_count),
    updatedCount: num(record.updated_count),
    quarantinedCount: num(record.quarantined_count),
    failedCount: num(record.failed_count),
    items: items.map((value) => {
      const item = (value ?? {}) as Record<string, unknown>
      return {
        nodeId: str(item.node_id),
        tagPath: str(item.tag_path),
        vendor: str(item.vendor),
        status: str(item.status),
        outcome: str(item.outcome),
        reason: str(item.reason)
      }
    })
  }
}

export async function registerTagsFromGraph(
  args: RegisterTagsFromGraphArgs,
  signal?: AbortSignal
): Promise<RegisterTagsFromGraphResult> {
  const body: Record<string, unknown> = {
    node_ids: args.nodeIds,
    preview: args.preview,
    transport: args.transport,
    frequency_ms: Math.floor(args.frequencyMs),
    enabled: args.enabled
  }
  if (args.containerScope) {
    body.container_scope = {
      id: args.containerScope.id,
      kind: args.containerScope.kind
    }
  }
  if (args.searchQuery?.trim()) body.search_query = args.searchQuery.trim()
  if (args.gatewayUrl?.trim()) body.gateway_url = args.gatewayUrl.trim()
  if (args.opcEndpointUrl?.trim()) body.opc_endpoint_url = args.opcEndpointUrl.trim()

  const response = await fetch(`${getOntologyServiceBaseUrl()}/tags/register-from-graph`, {
    method: 'POST',
    headers: createOntologyHeaders(),
    body: JSON.stringify(body),
    signal
  })
  const json = (await response.json().catch(() => ({}))) as Record<string, unknown>
  if (!response.ok) {
    throw new Error(
      str(json.error) ||
        str(json.message) ||
        `tag registration failed: ${response.status} ${response.statusText}`
    )
  }
  return registerTagsFromGraphResultFromJson(json)
}

interface RegisterTagsMutationState {
  isLoading: boolean
  error: string | undefined
}

export function useRegisterTagsFromGraphMutation(): [
  (args: RegisterTagsFromGraphArgs) => Promise<RegisterTagsFromGraphResult>,
  RegisterTagsMutationState
] {
  const [state, setState] = useState<RegisterTagsMutationState>({
    isLoading: false,
    error: undefined
  })
  const trigger = useCallback(async (args: RegisterTagsFromGraphArgs) => {
    setState({ isLoading: true, error: undefined })
    try {
      const result = await registerTagsFromGraph(args)
      if (!args.preview) invalidateTagAcquisitionQueries()
      setState({ isLoading: false, error: undefined })
      return result
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setState({ isLoading: false, error: message })
      throw error
    }
  }, [])
  return [trigger, state]
}
